import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useOrganizationStore } from '@/core/stores/organization.store';
import { useToast } from '@/shared/hooks/useToast';
import { useTranslation } from 'react-i18next';
import { tasksService } from '../api/tasks.service';
import { TASK_KEYS } from './queryKeys';
import { updateTaskEntity, removeTaskEntity } from './useTaskEntityCache';
import type {
  CreateTaskPayload,
  Task,
  UpdateTaskDetailPayload,
  CreateSubtaskPayload,
  UpdateSubtaskPayload,
} from '../types';

export function useUpdateTask(taskId: string) {
  const queryClient = useQueryClient();
  const orgId = useOrganizationStore((s) => s.activeOrganizationId)!;
  const { t } = useTranslation('tasks');
  const { toast } = useToast();

  return useMutation({
    mutationFn: (payload: UpdateTaskDetailPayload) => tasksService.update(taskId, payload),

    onMutate: async (payload) => {
      await queryClient.cancelQueries({ queryKey: TASK_KEYS.detail(orgId, taskId) });
      const previous = queryClient.getQueryData(TASK_KEYS.detail(orgId, taskId));
      const previousEntity = queryClient.getQueryData<Task>(TASK_KEYS.entity(orgId, taskId));

      updateTaskEntity(queryClient, orgId, taskId, (task) => {
        const updated: Task = { ...task };
        if (payload.title !== undefined) updated.title = payload.title;
        if (payload.description !== undefined) updated.description = payload.description;
        if (payload.priority !== undefined) updated.priority = payload.priority ?? undefined;
        if (payload.statusId !== undefined)
          updated.status = { ...task.status, id: payload.statusId };
        if (payload.startDate !== undefined) updated.startDate = payload.startDate ?? undefined;
        if (payload.dueDate !== undefined) updated.dueDate = payload.dueDate ?? undefined;
        if (payload.assigneeId !== undefined) {
          updated.assignee = payload.assigneeId
            ? { id: payload.assigneeId, name: '—', color: '' }
            : null;
        }
        if (payload.estimatedMinutes !== undefined)
          updated.estimatedMinutes = payload.estimatedMinutes;
        if (payload.groupId !== undefined) updated.groupId = payload.groupId ?? undefined;
        return updated;
      });

      return { previous, previousEntity };
    },

    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(TASK_KEYS.detail(orgId, taskId), context.previous);
      }
      if (context?.previousEntity) {
        updateTaskEntity(queryClient, orgId, taskId, () => context.previousEntity as Task);
      }
    },

    onSuccess: () => {
      toast.success({ title: t('dialog.updateSuccess') });
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: TASK_KEYS.detail(orgId, taskId) });
      queryClient.invalidateQueries({ queryKey: TASK_KEYS.activity(orgId, taskId, {}) });

      const entity = queryClient.getQueryData<Task>(TASK_KEYS.entity(orgId, taskId));
      if (entity?.projectId) {
        void queryClient.invalidateQueries({
          queryKey: TASK_KEYS.projectRoot(orgId, entity.projectId),
          type: 'active',
        });
      }
    },
  });
}

export function useCreateSubtask(parentTaskId: string) {
  const queryClient = useQueryClient();
  const orgId = useOrganizationStore((s) => s.activeOrganizationId)!;

  return useMutation({
    mutationFn: (payload: CreateSubtaskPayload) =>
      tasksService.createSubtask(parentTaskId, payload),

    onSuccess: () => {
      updateTaskEntity(queryClient, orgId, parentTaskId, (task) => ({
        ...task,
        subtaskCount: (task.subtaskCount ?? 0) + 1,
      }));
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: TASK_KEYS.detail(orgId, parentTaskId) });
      queryClient.invalidateQueries({ queryKey: TASK_KEYS.subtasks(orgId, parentTaskId) });
      queryClient.invalidateQueries({ queryKey: TASK_KEYS.treeRoot(orgId) });

      const entity = queryClient.getQueryData<Task>(TASK_KEYS.entity(orgId, parentTaskId));
      if (entity?.projectId) {
        void queryClient.invalidateQueries({
          queryKey: TASK_KEYS.projectRoot(orgId, entity.projectId),
          type: 'active',
        });
      }
    },
  });
}

export function useUpdateSubtask(parentTaskId: string) {
  const queryClient = useQueryClient();
  const orgId = useOrganizationStore((s) => s.activeOrganizationId)!;

  return useMutation({
    mutationFn: ({ subtaskId, payload }: { subtaskId: string; payload: UpdateSubtaskPayload }) => {
      // Map subtask-specific payload shape to the general update payload
      const mapped: UpdateTaskDetailPayload = {
        ...payload,
        title: payload.name,
      };
      return tasksService.update(subtaskId, mapped);
    },

    onMutate: async ({ subtaskId, payload }) => {
      const previousEntity = queryClient.getQueryData<Task>(TASK_KEYS.entity(orgId, subtaskId));

      updateTaskEntity(queryClient, orgId, subtaskId, (task) => {
        const updated: Task = { ...task };
        if (payload.name !== undefined) updated.title = payload.name;
        if (payload.priority !== undefined) updated.priority = payload.priority ?? undefined;
        if (payload.statusId !== undefined)
          updated.status = { ...task.status, id: payload.statusId };
        if (payload.assigneeId !== undefined) {
          updated.assignee = payload.assigneeId
            ? { id: payload.assigneeId, name: '—', color: '' }
            : null;
        }
        if (payload.startDate !== undefined) updated.startDate = payload.startDate ?? undefined;
        if (payload.dueDate !== undefined) updated.dueDate = payload.dueDate ?? undefined;
        if (payload.estimatedMinutes !== undefined)
          updated.estimatedMinutes = payload.estimatedMinutes;
        return updated;
      });

      return { previousEntity };
    },

    onError: (_err, _vars, context) => {
      if (context?.previousEntity) {
        updateTaskEntity(queryClient, orgId, _vars.subtaskId, () => context.previousEntity as Task);
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: TASK_KEYS.detail(orgId, parentTaskId) });
      queryClient.invalidateQueries({ queryKey: TASK_KEYS.subtasks(orgId, parentTaskId) });
      queryClient.invalidateQueries({ queryKey: TASK_KEYS.treeRoot(orgId) });
      const parent = queryClient.getQueryData<Task>(TASK_KEYS.entity(orgId, parentTaskId));
      if (parent?.projectId) {
        queryClient.invalidateQueries({ queryKey: TASK_KEYS.projectRoot(orgId, parent.projectId) });
      }
    },
  });
}

export function useDeleteSubtask(parentTaskId: string) {
  const queryClient = useQueryClient();
  const orgId = useOrganizationStore((s) => s.activeOrganizationId)!;

  return useMutation({
    mutationFn: (subtaskId: string) => tasksService.deleteTask(subtaskId),

    onSuccess: (_data, subtaskId) => {
      removeTaskEntity(queryClient, orgId, subtaskId);
      updateTaskEntity(queryClient, orgId, parentTaskId, (task) => ({
        ...task,
        subtaskCount: Math.max(0, (task.subtaskCount ?? 0) - 1),
      }));
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: TASK_KEYS.detail(orgId, parentTaskId) });
      queryClient.invalidateQueries({ queryKey: TASK_KEYS.subtasks(orgId, parentTaskId) });
      queryClient.invalidateQueries({ queryKey: TASK_KEYS.treeRoot(orgId) });
      const parent = queryClient.getQueryData<Task>(TASK_KEYS.entity(orgId, parentTaskId));
      if (parent?.projectId) {
        queryClient.invalidateQueries({ queryKey: TASK_KEYS.projectRoot(orgId, parent.projectId) });
      }
    },
  });
}

export function useUploadAttachment(taskId: string) {
  const queryClient = useQueryClient();
  const orgId = useOrganizationStore((s) => s.activeOrganizationId)!;

  return useMutation({
    mutationFn: (file: File) => tasksService.uploadAttachment(taskId, file),

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: TASK_KEYS.attachments(orgId, taskId) });
    },
  });
}

export function useDeleteAttachment(taskId: string) {
  const queryClient = useQueryClient();
  const orgId = useOrganizationStore((s) => s.activeOrganizationId)!;

  return useMutation({
    mutationFn: (attachmentId: string) => tasksService.deleteAttachment(taskId, attachmentId),

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: TASK_KEYS.attachments(orgId, taskId) });
    },
  });
}

/**
 * Creates a task from nothing but a title, on the board's first column.
 *
 * The board view has its own create path inside `useTaskBoard`, but that one is
 * bound to the column caches of the filters currently on screen. This hook is
 * for callers outside the board — the toolbar's quick-create panel — so it only
 * invalidates and lets the views refetch.
 */
export function useCreateBoardTask(projectId: string, boardId: string) {
  const queryClient = useQueryClient();
  const orgId = useOrganizationStore((s) => s.activeOrganizationId)!;

  return useMutation({
    mutationFn: (payload: Omit<CreateTaskPayload, 'boardId'>) =>
      tasksService.create(projectId, { ...payload, boardId }),

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: TASK_KEYS.projectRoot(orgId, projectId) });
    },
  });
}
